/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';
import { ChevronDown, Menu, X } from 'lucide-preact';

/*----------------------------------
- APP
----------------------------------*/

import {
    MARKETING_LOGO_URL,
    MARKETING_PARTNER_ITEMS,
    MARKETING_SOLUTION_ITEMS,
    MARKETING_URLS,
    type MarketingPageId,
} from '@/client/catalogs/marketing/navigation';

/*----------------------------------
- TYPES
----------------------------------*/

export interface PublicHeaderProps {
    currentPage?: MarketingPageId;
    ctaLabel?: string;
    ctaHref?: string;
}

/*----------------------------------
- CONSTANTS
----------------------------------*/

const HEADER_SOLUTION_ITEMS = MARKETING_SOLUTION_ITEMS.filter(item => item.id !== 'saas');

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function PublicHeader({
    currentPage = 'saas',
    ctaLabel = 'Get Started',
    ctaHref = MARKETING_URLS.login,
}: PublicHeaderProps) {
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
    const [solutionsOpen, setSolutionsOpen] = React.useState(false);
    const [partnersOpen, setPartnersOpen] = React.useState(false);
    const closeSolutionsTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const closePartnersTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTimeoutRef = React.useCallback((timeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const closeAllMenus = React.useCallback(() => {
        setMobileMenuOpen(false);
        setSolutionsOpen(false);
        setPartnersOpen(false);
    }, []);

    const openSolutions = React.useCallback(() => {
        clearTimeoutRef(closeSolutionsTimeoutRef);
        setSolutionsOpen(true);
    }, [clearTimeoutRef]);

    const closeSolutions = React.useCallback(() => {
        clearTimeoutRef(closeSolutionsTimeoutRef);
        closeSolutionsTimeoutRef.current = setTimeout(() => {
            setSolutionsOpen(false);
        }, 150);
    }, [clearTimeoutRef]);

    const openPartners = React.useCallback(() => {
        clearTimeoutRef(closePartnersTimeoutRef);
        setPartnersOpen(true);
    }, [clearTimeoutRef]);

    const closePartners = React.useCallback(() => {
        clearTimeoutRef(closePartnersTimeoutRef);
        closePartnersTimeoutRef.current = setTimeout(() => {
            setPartnersOpen(false);
        }, 150);
    }, [clearTimeoutRef]);

    React.useEffect(() => () => {
        clearTimeoutRef(closeSolutionsTimeoutRef);
        clearTimeoutRef(closePartnersTimeoutRef);
    }, [clearTimeoutRef]);

    return (
        <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-orange-100 z-50 shadow-sm">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
                <div className="flex items-center justify-between">
                    <a href={MARKETING_URLS.home} className="flex items-center gap-3">
                        <img src={MARKETING_LOGO_URL} alt="CrossPath Logo" className="w-10 h-10" />
                        <span className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent">
                            CrossPath
                        </span>
                    </a>

                    <nav className="hidden md:flex items-center gap-8">
                        <div
                            className="relative"
                            onMouseEnter={openSolutions}
                            onMouseLeave={closeSolutions}
                        >
                            <button
                                type="button"
                                className={`font-medium transition-all flex items-center gap-1 py-2 ${
                                    MARKETING_SOLUTION_ITEMS.some(item => item.id === currentPage)
                                        ? 'text-orange-600'
                                        : 'text-slate-700 hover:text-orange-600'
                                }`}
                            >
                                Solutions
                                <ChevronDown size={16} className={`transition-transform ${solutionsOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {solutionsOpen && (
                                <div
                                    className="absolute top-full left-1/2 -translate-x-1/2 pt-2"
                                    onMouseEnter={openSolutions}
                                    onMouseLeave={closeSolutions}
                                >
                                    <div className="w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
                                        <div className="p-6">
                                            <h3 className="text-sm font-bold text-slate-500 mb-4 uppercase tracking-wide">
                                                Solutions
                                            </h3>
                                            <div className="space-y-1">
                                                {HEADER_SOLUTION_ITEMS.map((item) => {
                                                    const Icon = item.icon;
                                                    const isCurrent = item.id === currentPage;

                                                    return (
                                                        <a
                                                            key={item.href}
                                                            href={item.href}
                                                            onClick={closeAllMenus}
                                                            className={`w-full flex items-start gap-3 p-3 rounded-lg transition-all text-left ${
                                                                isCurrent
                                                                    ? 'bg-orange-50 text-orange-600'
                                                                    : 'hover:bg-slate-50 text-slate-700'
                                                            }`}
                                                        >
                                                            <div
                                                                className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                                                    isCurrent ? 'bg-orange-100' : 'bg-slate-100'
                                                                }`}
                                                            >
                                                                <Icon size={20} className={isCurrent ? 'text-orange-600' : 'text-slate-600'} />
                                                            </div>
                                                            <div>
                                                                <div className="font-semibold mb-0.5">{item.label}</div>
                                                                <div className="text-xs text-slate-500">{item.description}</div>
                                                            </div>
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div
                            className="relative"
                            onMouseEnter={openPartners}
                            onMouseLeave={closePartners}
                        >
                            <button
                                type="button"
                                className="font-medium transition-all flex items-center gap-1 py-2 text-slate-700 hover:text-orange-600"
                            >
                                Partners
                                <ChevronDown size={16} className={`transition-transform ${partnersOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {partnersOpen && (
                                <div
                                    className="absolute top-full left-1/2 -translate-x-1/2 pt-2"
                                    onMouseEnter={openPartners}
                                    onMouseLeave={closePartners}
                                >
                                    <div className="w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
                                        <div className="p-6">
                                            <h3 className="text-sm font-bold text-slate-500 mb-4 uppercase tracking-wide">
                                                Partner With Us
                                            </h3>
                                            <div className="space-y-1">
                                                {MARKETING_PARTNER_ITEMS.map((item) => {
                                                    const Icon = item.icon;

                                                    return (
                                                        <a
                                                            key={item.href}
                                                            href={item.href}
                                                            onClick={closeAllMenus}
                                                            className="w-full flex items-start gap-3 p-3 rounded-lg transition-all text-left hover:bg-slate-50 text-slate-700"
                                                        >
                                                            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100">
                                                                <Icon size={20} className="text-slate-600" />
                                                            </div>
                                                            <div>
                                                                <div className="font-semibold mb-0.5">{item.label}</div>
                                                                <div className="text-xs text-slate-500">{item.description}</div>
                                                            </div>
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </nav>

                    <div className="hidden md:flex items-center gap-3">
                        <a
                            href={MARKETING_URLS.login}
                            className="text-slate-700 hover:text-orange-600 font-medium transition-colors px-4 py-2"
                        >
                            Login
                        </a>
                        <a
                            href={ctaHref}
                            className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white px-6 py-2.5 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                        >
                            {ctaLabel}
                        </a>
                    </div>

                    <button
                        type="button"
                        onClick={() => setMobileMenuOpen(prev => !prev)}
                        className="md:hidden p-2"
                        aria-label="Toggle menu"
                    >
                        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>

                {mobileMenuOpen && (
                    <div className="md:hidden mt-4 pb-4 border-t border-orange-100 pt-4">
                        <nav className="flex flex-col gap-4">
                            <div>
                                <button
                                    type="button"
                                    onClick={() => setSolutionsOpen(prev => !prev)}
                                    className="w-full flex items-center justify-between font-medium text-slate-700 mb-2"
                                >
                                    Solutions
                                    <ChevronDown size={16} className={`transition-transform ${solutionsOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {solutionsOpen && (
                                    <div className="ml-4 space-y-2">
                                        {HEADER_SOLUTION_ITEMS.map((item) => {
                                            const Icon = item.icon;
                                            const isCurrent = item.id === currentPage;

                                            return (
                                                <a
                                                    key={item.href}
                                                    href={item.href}
                                                    onClick={closeAllMenus}
                                                    className={`flex items-center gap-3 py-2 w-full text-left ${
                                                        isCurrent ? 'text-orange-600' : 'text-slate-600 hover:text-orange-600'
                                                    }`}
                                                >
                                                    <Icon size={18} />
                                                    <span className="text-sm">{item.label}</span>
                                                </a>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <div>
                                <button
                                    type="button"
                                    onClick={() => setPartnersOpen(prev => !prev)}
                                    className="w-full flex items-center justify-between font-medium text-slate-700 mb-2"
                                >
                                    Partners
                                    <ChevronDown size={16} className={`transition-transform ${partnersOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {partnersOpen && (
                                    <div className="ml-4 space-y-2">
                                        {MARKETING_PARTNER_ITEMS.map((item) => {
                                            const Icon = item.icon;

                                            return (
                                                <a
                                                    key={item.href}
                                                    href={item.href}
                                                    onClick={closeAllMenus}
                                                    className="flex items-center gap-3 py-2 w-full text-left text-slate-600 hover:text-orange-600"
                                                >
                                                    <Icon size={18} />
                                                    <span className="text-sm">{item.label}</span>
                                                </a>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-col gap-3 pt-2 border-t border-orange-100">
                                <a
                                    href={MARKETING_URLS.login}
                                    onClick={closeAllMenus}
                                    className="text-slate-700 hover:text-orange-600 font-medium text-left"
                                >
                                    Login
                                </a>
                                <a
                                    href={ctaHref}
                                    onClick={closeAllMenus}
                                    className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-6 py-2.5 rounded-xl font-semibold shadow-lg text-center"
                                >
                                    {ctaLabel}
                                </a>
                            </div>
                        </nav>
                    </div>
                )}
            </div>
        </header>
    );
}
